import { useState, useCallback, useEffect } from 'react';
import { copyText } from '../utils/clipboard';
import './FieldCopyButton.css';

/**
 * Shared per-field copy control (2026-07-05). Reuses the ContentCard copy-icon
 * pattern (same SVG, same `card-copy-btn` styling, same "Copied!" flash) for
 * fields that are NOT rendered through ContentCard — Final Review summary
 * rows, context fields above card grids, compare-mode cards. Clipboard API
 * with execCommand fallback via utils/clipboard.copyText.
 *
 * Reveal-on-hover: give the field's container the `field-copy-host` class.
 * The click never selects/regenerates/navigates — propagation is stopped.
 */
export default function FieldCopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!text) return;
    void copyText(text).then((ok) => {
      if (ok) setCopied(true);
    });
  }, [text]);

  return (
    <span className="field-copy-wrap">
      {copied && <span className="card-copy-flash" aria-live="polite">Copied!</span>}
      <button
        type="button"
        className={`card-copy-btn field-copy-btn ${copied ? 'copied' : ''}`}
        onClick={handleCopy}
        onMouseDown={(e) => e.stopPropagation()}
        title={label || 'Copy to clipboard'}
        aria-label={label || 'Copy to clipboard'}
      >
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2.5 7.5l3 3 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <rect x="4" y="3" width="7" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M3 10V2.5a1 1 0 0 1 1-1h5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        )}
      </button>
    </span>
  );
}
